import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import ViewRegisterModal from '../../components/Modal/ProfitLoss';
import { PeriodModeToggle, ReportPeriodMode, reportDatePickerProps } from '../../components/ReportPeriod/ReportPeriod';
import ScrollToggle from '../../components/ScrollToggle/ScrollToggle';
import CalculatingLoader from '../../components/CalculatingLoader/CalculatingLoader';
import { MONTHS } from '../../resources/constants';
import { calculateMaxHeightContent, fileDownloadBlob, lastYears } from '../../resources/functions';
import { currencyFormat } from '../../resources/helper';
import { useReportApi } from '../../resources/hooks/api/useReportApiHook';
import { getBranch } from '../../store/user/selectors';
import './ProfitLoss.css';

interface Props {

}

const COLUMN_OPTIONS = [
    { key: 'current_month', label: 'Current Month' },
    { key: 'ytd', label: 'Year To Date' },
    { key: 'current_month_prior_year', label: 'Current Month Prior Year' },
    { key: 'ytd_prior_year', label: 'Year To Date Prior Year' },
];
const ALL_COLUMN_KEYS = COLUMN_OPTIONS.map(c => c.key);

// The period a report was generated for. Headers, the View Register window and the PDF request
// read this snapshot, never the live filter controls.
type PnlPeriod =
    | { mode: 'monthly'; month: string; year: number }
    | { mode: 'custom'; startDate: string; endDate: string }; // YYYY-MM-DD

const isValidDate = (date: Date | null): date is Date => date instanceof Date && !isNaN(date.getTime());

// Statement hierarchy by report_code: GH / SH headers, VP accounts, ST / GT totals, and the statement
// totals TT (Net Operating Revenue, Gross Profit), CT (EBITDA) and RGT (Income (Loss) Before Income
// Taxes). A total's level matches the header it closes.
const HEADER_LEVELS: Record<string, number> = { GH: 0, SH: 1 };
const TOTAL_LEVELS: Record<string, number> = { GT: 0, TT: 0, CT: 0, RGT: 0, ST: 1 };
const ROW_VARIANTS: Record<string, string> = {
    GH: 'group',
    SH: 'section',
    ST: 'section-total',
    GT: 'group-total',
    TT: 'statement-total',
    CT: 'statement-total',
    RGT: 'net-total',
};

type PnlRow = { data: any; code: string; level: number };

// Rows hidden exactly as before: accounts with nothing to show in the mode's columns
const isHiddenCustomRow = (data: any) =>
    ['VP', 'VP/IP', 'IP'].includes(data['account_chart']['report_code']) && !data['total_amount'] && !data['ratio'];
const isHiddenMonthlyRow = (data: any) =>
    ['VP', 'VP/IP', 'IP'].includes(data['account_chart']['report_code']) && !data['total_amount'] && !data['current_year_budget_amount'] && !data['ytd_total_amount'] && !data['budget_ytd_total_amount'] && !data['prev_year_total_amount'] && !data['budget_prev_year_total_amount'] && !data['prev_ytd_total_amount'] && !data['budget_prev_ytd_total_amount'];

// Unchanged sign convention: accounts 9902-9964 show positive amounts in parentheses, all other
// accounts show negative amounts in parentheses
const formatAmount = (value: any, account: number) =>
    ((account >= 9902) && (account <= 9964)) ?
        (value <= 0 ? currencyFormat((value || 0).toFixed(2)) : `(${currencyFormat(Math.abs((value || 0).toFixed(2)))})`) :
        (value >= 0 ? currencyFormat((value || 0).toFixed(2)) : `(${currencyFormat(Math.abs((value || 0).toFixed(2)))})`);

// A figure without a non-zero digit ($ 0.00, 0.00%) is shown muted
const isZeroFigure = (text: string) => !/[1-9]/.test(text);

// Column-group dates, e.g. "Jan 1, 2025 – Jan 31, 2025"
const formatColumnRange = (start: string, end: string) =>
    `${moment(start, 'YYYY-MM-DD').format('MMM D, YYYY')} – ${moment(end, 'YYYY-MM-DD').format('MMM D, YYYY')}`;

const ProfitLoss: React.FC<Props> = () => {
    const { profitLossData, downloadReport } = useReportApi();
    const { t } = useTranslation("screens.profitLoss");

    const [glProfitLossList, setGlProfitLossList] = useState<any[]>([]);
    const [searchParams, setSearchParams] = useState<any>({
        month: { value: '01', label:  t('month.01', { ns: 'resources.form-validator' }) },
        year: { value: new Date().getFullYear(), label: '' + new Date().getFullYear() }
    });
    const [periodMode, setPeriodMode] = useState<ReportPeriodMode>('monthly');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [generatedPeriod, setGeneratedPeriod] = useState<PnlPeriod | null>(null);
    // true only after a Generate completed successfully with no report rows; before that the empty
    // table shows the neutral "select a period" prompt instead of a no-data message
    const [lastGenerateEmpty, setLastGenerateEmpty] = useState<boolean>(false);
    const periodModeOptions: { value: ReportPeriodMode; label: string }[] = [
        { value: 'monthly', label: t('filter.periodMode.monthly') },
        { value: 'custom', label: t('filter.periodMode.custom') },
    ];
    // Monthly headers and View Register use the generated month; before any report is generated
    // the selected filters only label the empty table.
    const month: any = generatedPeriod?.mode === 'monthly' ? generatedPeriod.month : searchParams.month['value'];
    const year: any = generatedPeriod?.mode === 'monthly' ? generatedPeriod.year : searchParams.year['value'];
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isThirdPartyEnabled, setIsThirdPartyEnabled] = useState<boolean>(false);
    const [isShowViewRegister, setIsShowViewRegister] = useState<boolean>(false);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
    const [showExportPopover, setShowExportPopover] = useState<boolean>(false);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(ALL_COLUMN_KEYS);
    const popoverRef = useRef<HTMLDivElement>(null);
    const exportBtnRef = useRef<HTMLButtonElement>(null);
    const [txnRegisterPayload, setTxnRegisterPayload] = useState<{
        startDate: string,
        endDate: string,
        account: number,
        hasEditAccess: boolean
    }>({
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
        account: 0,
        hasEditAccess: false
    });

    const branchData = useSelector(getBranch)
 const tableContainerRef = useRef<HTMLDivElement>(null)
    // Collapsed group / section headers by account_chart id (view-only state)
    const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(78));
    })

    // Filter controls are a draft for the NEXT Generate; the visible report stays tied to
    // generatedPeriod until a Generate succeeds. draftPeriod is null while a custom date is missing/invalid.
    const draftPeriod: PnlPeriod | null = periodMode === 'custom'
        ? (isValidDate(startDate) && isValidDate(endDate)
            ? { mode: 'custom', startDate: moment(startDate).format('YYYY-MM-DD'), endDate: moment(endDate).format('YYYY-MM-DD') }
            : null)
        : { mode: 'monthly', month: searchParams.month['value'], year: searchParams.year['value'] };

    // Simple input checks before Generate (typed input included); the backend stays authoritative
    // for the period and its gates
    const draftPeriodError = (): string | null => {
        if (periodMode !== 'custom') return null;
        if (!startDate || !endDate) return 'filter.dateRangeRequired';
        if (!isValidDate(startDate) || !isValidDate(endDate)) return 'filter.dateInvalid';
        if (moment(startDate).isAfter(moment(), 'day') || moment(endDate).isAfter(moment(), 'day')) return 'filter.dateInFuture';
        if (moment(startDate).isAfter(endDate, 'day')) return 'filter.dateRangeInvalid';
        return null;
    }

    // Generate (no argument) validates and sends the draft. Closing the View Register re-fetches
    // the generated period it was opened from, never the draft.
    const fetchProfitLossList = (periodToFetch?: PnlPeriod) => {
        if (!periodToFetch) {
            const draftError = draftPeriodError();
            if (draftError) {
                toast.error(t(draftError));
                return;
            }
        }
        const requestedPeriod = (periodToFetch ?? draftPeriod) as PnlPeriod;
        const params: any = requestedPeriod.mode === 'custom'
            ? {
                "page": 1,
                "per_page": 5000,
                start_date: requestedPeriod.startDate,
                end_date: requestedPeriod.endDate,
                branch_id: +branchData['id']
            }
            : {
                "page": 1,
                "per_page": 5000,
                year: requestedPeriod.year,
                month: requestedPeriod.month,
                branch_id: +branchData['id']
            }

        setIsLoading(true);
        profitLossData(params, (message: string, resp: any) => {
            setIsLoading(false);
            if (resp && resp['data'] && resp['data']['data'] && resp['data']['data']['result']) {
                setGeneratedPeriod(requestedPeriod);
                setGlProfitLossList(resp.data.data.result);
                setLastGenerateEmpty(!resp.data.data.result.length);
            } else {
                setGeneratedPeriod(null);
                setGlProfitLossList([]);
                setLastGenerateEmpty(true);
            }

            if (resp && resp['data'] && resp['data']['data']) {
                setIsThirdPartyEnabled(resp.data.data?.is_third_party_enable ?? false)
            }

        }, (message: string, resp: any) => {
            setGeneratedPeriod(null);
            setGlProfitLossList([]);
            setLastGenerateEmpty(false);
            setIsLoading(false);
            toast.error(message);
        })
    }

    // Switching Monthly <-> Custom Range changes the report's presentation, so the generated report
    // is cleared and Export waits for a Generate in the new mode.
    const _handleChangePeriodMode = (mode: ReportPeriodMode) => {
        setGlProfitLossList([]);
        setGeneratedPeriod(null);
        setLastGenerateEmpty(false);
        setShowExportPopover(false);
        setPeriodMode(mode);
    }

    // Date / month / year edits only change the draft: the visible report, its headers, View Register
    // and Export stay on generatedPeriod until Generate succeeds. No report request is sent here.
    const _handleChangeDate = (date: Date | null, type: 'start' | 'end') => {
        if (type === 'start') {
            setStartDate(date);
        } else {
            setEndDate(date);
        }
    }

    const _handleChangeSearchParam = (e: any, type: string) => {
        if (type === 'month') {
            setSearchParams({
                ...searchParams,
                'month': e
            })
        } else if (type === 'year') {
            setSearchParams({
                ...searchParams,
                'year': e
            })
        }
    }
    const getLastDayOfMonth = (month: any, year: any) => {
        return new Date(year, month, 0).getDate();
    }
    const percentageFormat = (value: any) => {
        return `${value}%`;
    };

    const calculateRatio = (amount: any, budget: any) => {
        // Ensure numerator and denominator are not zero to avoid division by zero error
        if (budget !== 0) {
            const ratio = (amount / budget) * 100;
            return percentageFormat(ratio.toFixed(2));
        } else {
            // Handle division by zero error gracefully
            return percentageFormat((0).toFixed(2))
        }
    };

    const _toggleExportPopover = () => {
        setShowExportPopover(prev => {
            if (!prev) {
                setSelectedColumns(ALL_COLUMN_KEYS);
                return true;
            }
            return false;
        });
    };

    const _closeExportPopover = () => {
        setShowExportPopover(false);
        exportBtnRef.current?.focus();
    };

    const _toggleColumn = (key: string) => {
        setSelectedColumns(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const _toggleAllColumns = () => {
        setSelectedColumns(prev =>
            prev.length === ALL_COLUMN_KEYS.length ? [] : [...ALL_COLUMN_KEYS]
        );
    };

    // Backend PDF (Jinja2 + WeasyPrint, searchable text) for the generated period
    const _downloadReport = (columnsToExport: string[] = selectedColumns) => {
        if (!generatedPeriod) return;
        let params: any;
        let fileName: string;
        if (generatedPeriod.mode === 'custom') {
            // Custom range: Account / Amount / Ratio only, so no column selection
            params = {
                start_date: generatedPeriod.startDate,
                end_date: generatedPeriod.endDate,
                branch_id: +branchData['id'],
                type: 2 // for profit/loss
            }
            fileName = `ps-${generatedPeriod.startDate}-to-${generatedPeriod.endDate}.pdf`;
        } else {
            if (!columnsToExport.length) return;
            params = {
                year: generatedPeriod.year,
                month: +generatedPeriod.month,
                branch_id: +branchData['id'],
                type: 2, // for profit/loss
                columns: columnsToExport.join(',')
            }
            fileName = `ps-${generatedPeriod.year}-${generatedPeriod.month}`;
        }

        setIsLoading(true);
        setShowExportPopover(false);
        downloadReport(params, (message: string, resp: any) => {
            setIsLoading(false);
            fileDownloadBlob(resp, fileName)
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }

    useEffect(() => {
        if (!showExportPopover) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                _closeExportPopover();
            }
        };
        const handleClickOutside = (e: MouseEvent) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target as Node) &&
                exportBtnRef.current &&
                !exportBtnRef.current.contains(e.target as Node)
            ) {
                setShowExportPopover(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showExportPopover]);

    const _handleShowViewRegister = (data: any, startDate: string, hasEditAccess: boolean) => {
        setTxnRegisterPayload({
            startDate: startDate,
            // Custom range: exactly the generated start..end; monthly: unchanged (month end)
            endDate: generatedPeriod?.mode === 'custom' ? generatedPeriod.endDate : `${year}-${month}-${getLastDayOfMonth(month, year)}`,
            account: data['account_chart']['id'],
            hasEditAccess: isThirdPartyEnabled ? false : hasEditAccess
        })
        setIsShowViewRegister(true);
    }

    const _handleCloseViewRegister = () => {
        setIsShowViewRegister(false);
        if (generatedPeriod) {
            fetchProfitLossList(generatedPeriod);
        }
    }

    const _toggleCollapsed = (id: number) => {
        setCollapsedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    // Display rows: hidden rows skipped as before, indent level taken from the nearest header, and rows
    // under a collapsed header skipped up to its matching total (which stays visible).
    const reportRows = (() => {
        const rows: PnlRow[] = [];
        let headerLevel = 0;
        let collapsedLevel: number | null = null;
        glProfitLossList.forEach((data: any) => {
            if (periodMode === 'custom' ? isHiddenCustomRow(data) : isHiddenMonthlyRow(data)) return;
            const code = data['account_chart']['report_code'];
            const header = HEADER_LEVELS[code];
            const total = TOTAL_LEVELS[code];
            if (collapsedLevel !== null) {
                const boundary = header ?? total;
                if (boundary === undefined || boundary > collapsedLevel) return;
                collapsedLevel = null;
            }
            let level = headerLevel + 1;
            if (header !== undefined) {
                level = header;
                headerLevel = header;
                if (collapsedIds.has(data['account_chart']['id'])) collapsedLevel = header;
            } else if (total !== undefined) {
                level = total;
                headerLevel = Math.max(total - 1, 0);
            }
            rows.push({ data, code, level });
        });
        return rows;
    })();

    // Monthly column groups, dated from the generated month (the draft month before any Generate) as before
    const monthEnd = `${year}-${month}-${getLastDayOfMonth(month, year)}`;
    const priorMonthEnd = `${year - 1}-${month}-${getLastDayOfMonth(month, year - 1)}`;
    // kind only sizes the column (pl-col-sub--ratio / --amount / --budget)
    const ratioColumn = { label: t("table.ratio"), kind: 'ratio' };
    const amountColumn = { label: t("table.amount"), kind: 'amount' };
    const budgetColumns = [ratioColumn, amountColumn, { label: t("table.budget"), kind: 'budget' }, ratioColumn];
    const priorYearColumns = [ratioColumn, amountColumn];
    const monthlyColumnGroups = [
        { label: t("table.currentMonth"), range: formatColumnRange(`${year}-${month}-01`, monthEnd), columns: budgetColumns },
        { label: t("table.yearToDate"), range: formatColumnRange(`${year}-01-01`, monthEnd), columns: budgetColumns },
        { label: t("table.currentMonthPriorYear"), range: formatColumnRange(`${year - 1}-${month}-01`, priorMonthEnd), columns: priorYearColumns },
        { label: t("table.yearToDatePriorYear"), range: formatColumnRange(`${year - 1}-01-01`, priorMonthEnd), columns: priorYearColumns },
    ];
    const customColumnRange = generatedPeriod?.mode === 'custom' ? formatColumnRange(generatedPeriod.startDate, generatedPeriod.endDate) : '';
    const reportPeriodLabel = generatedPeriod
        ? t("table.reportPeriod", generatedPeriod.mode === 'custom'
            ? { start: moment(generatedPeriod.startDate, 'YYYY-MM-DD').format('LL'), end: moment(generatedPeriod.endDate, 'YYYY-MM-DD').format('LL') }
            : { start: moment(`${year}-${month}-01`, 'YYYY-MM-DD').format('LL'), end: moment(monthEnd, 'YYYY-MM-DD').format('LL') })
        : '';
    const hasReport = !!(glProfitLossList.length && generatedPeriod);

    const renderFigure = (text: string, onClick?: () => void) => {
        const className = `pl-figure${isZeroFigure(text) ? ' pl-figure--zero' : ''}`;
        return onClick ? (
            <button type="button" onClick={onClick} className={`${className} pl-figure--link`}>{text}</button>
        ) : (
            <span className={className}>{text}</span>
        );
    }

    const renderDescription = ({ data, code, level }: PnlRow) => {
        const id = data['account_chart']['id'];
        // "**" refers to the EBITDA note below the table
        const name = `${data['account_chart']['name']}${data['account_chart']['account'] === 100000 ? "**" : ""}`;
        return (
            <th scope="row" className="pl-cell--desc" style={{ '--pl-level': level } as React.CSSProperties}>
                {HEADER_LEVELS[code] !== undefined ? (
                    <button
                        type="button"
                        className="pl-row-toggle"
                        aria-expanded={!collapsedIds.has(id)}
                        onClick={() => _toggleCollapsed(id)}
                    >
                        <i className="fa-solid fa-chevron-down pl-chevron" aria-hidden="true"></i>
                        {name}
                    </button>
                ) : name}
            </th>
        );
    }

    const renderEmptyRow = (colSpan: number, message: string) => (
        <tr>
            <td colSpan={colSpan} className="pl-empty">
                <i className="fa-regular fa-file-lines" aria-hidden="true"></i>
                {message}
            </td>
        </tr>
    );

    return (
        <div className="main-container flex-grow-1 pl-page">
            <div className="container-fluid">
                <div className="pl-page-header">
                    <div className="pl-title-block">
                        <div className="pl-title-icon" aria-hidden="true">
                            <i className="fa-regular fa-file-lines"></i>
                        </div>
                        <div>
                            <p className="pl-eyebrow">{t("page.eyebrow")}</p>
                            <h1 className="pl-title">{t("page.title")}</h1>
                            {/* Period of the report on screen (generatedPeriod), never the draft filters */}
                            {generatedPeriod
                                ? <p className="pl-period">{reportPeriodLabel}</p>
                                : <p className="pl-subtitle">{t("page.subtitle")}</p>}
                        </div>
                    </div>
                    <div className="report-period-toolbar pl-toolbar">
                        <PeriodModeToggle
                            options={periodModeOptions}
                            value={periodMode}
                            onChange={_handleChangePeriodMode}
                            ariaLabel={t("page.title")}
                        />
                        <div className="report-period-fields d-flex align-items-center gap-2">
                            {periodMode === 'monthly' ? (
                                <>
                                    <div className="filter-box report-period-field">
                                        <Select
                                            onChange={(e: any) => _handleChangeSearchParam(e, 'month')}
                                            options={MONTHS}
                                            defaultValue={searchParams['month']}
                                            className={'zindex-10'}
                                            classNamePrefix="report-period-select"
                                            aria-label={t("filter.month")}
                                        />
                                    </div>
                                    <div className="filter-box report-period-field report-period-field--year">
                                        <Select
                                            onChange={(e: any) => _handleChangeSearchParam(e, 'year')}
                                            options={lastYears(5)}
                                            defaultValue={searchParams['year']}
                                            className={'zindex-10'}
                                            classNamePrefix="report-period-select"
                                            aria-label={t("filter.year")}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="filter-box report-period-field pl-date-field">
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date: Date | null) => _handleChangeDate(date, 'start')}
                                            className="form-control"
                                            dateFormat={'MM/dd/yyyy'}
                                            placeholderText={t("filter.startDate")}
                                            {...reportDatePickerProps}
                                            maxDate={isValidDate(endDate) && endDate < new Date() ? endDate : new Date()}
                                            showYearDropdown
                                            scrollableYearDropdown
                                            yearDropdownItemNumber={40}
                                            showMonthDropdown
                                        />
                                        <i className="fa-regular fa-calendar pl-date-field__icon" aria-hidden="true"></i>
                                    </div>
                                    <div className="filter-box report-period-field pl-date-field">
                                        <DatePicker
                                            selected={endDate}
                                            onChange={(date: Date | null) => _handleChangeDate(date, 'end')}
                                            className="form-control"
                                            dateFormat={'MM/dd/yyyy'}
                                            placeholderText={t("filter.endDate")}
                                            minDate={startDate ?? undefined}
                                            maxDate={new Date()}
                                            showYearDropdown
                                            scrollableYearDropdown
                                            yearDropdownItemNumber={40}
                                            showMonthDropdown
                                            {...reportDatePickerProps}
                                        />
                                        <i className="fa-regular fa-calendar pl-date-field__icon" aria-hidden="true"></i>
                                    </div>
                                </>
                            )}
                        </div>

                        <button type="button" onClick={() => fetchProfitLossList()} className="btn btn-primary pl-toolbar-btn">
                            <i className="fa-solid fa-play" aria-hidden="true"></i>
                            {t("filter.generateReport")}
                        </button>
                        {/* Always rendered (disabled until a report exists) so the toolbar width never changes */}
                        <div className="pl-export" title={hasReport ? undefined : t("filter.exportPdfHint")}>
                            <button
                                ref={exportBtnRef}
                                type="button"
                                onClick={generatedPeriod?.mode === 'custom' ? () => _downloadReport() : _toggleExportPopover}
                                disabled={!hasReport}
                                className="btn pl-toolbar-btn pl-btn-export"
                                aria-haspopup="dialog"
                                aria-expanded={showExportPopover}
                            >
                                <i className="fa-solid fa-download" aria-hidden="true"></i>
                                {t("filter.exportPdf")}
                            </button>

                            {hasReport && showExportPopover && generatedPeriod?.mode === 'monthly' && (
                                <div
                                    ref={popoverRef}
                                    role="dialog"
                                    aria-label="Select columns to include in PDF export"
                                    className="pdf-export-popover"
                                >
                                    <p className="pdf-export-popover__title">
                                        Select columns to include
                                    </p>

                                    <label className="pdf-export-popover__select-all">
                                        <input
                                            type="checkbox"
                                            checked={selectedColumns.length === ALL_COLUMN_KEYS.length}
                                            onChange={_toggleAllColumns}
                                            aria-label="Select all or deselect all columns"
                                        />
                                        Select All / Deselect All
                                    </label>

                                    {COLUMN_OPTIONS.map(col => (
                                        <label key={col.key} className="pdf-export-popover__option">
                                            <input
                                                type="checkbox"
                                                checked={selectedColumns.includes(col.key)}
                                                onChange={() => _toggleColumn(col.key)}
                                                aria-label={col.label}
                                            />
                                            {col.label}
                                        </label>
                                    ))}

                                    {(selectedColumns.length === 0) && (
                                        <p className="pdf-export-popover__error" role="alert">
                                            Select at least one column
                                        </p>
                                    )}

                                    <div className="pdf-export-popover__footer">
                                        <button
                                            type="button"
                                            className="btn btn-cancel btn-sm"
                                            onClick={_closeExportPopover}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            disabled={selectedColumns.length === 0}
                                            onClick={() => _downloadReport(selectedColumns)}
                                        >
                                            Export PDF
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Fixed full-page overlay; kept outside the toolbar so it takes no flex slot */}
                <div className="filter-btn">
                    <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                        <CalculatingLoader variant="primary" size={40} label={t("loader.generatingReport")} />
                    </div>
                </div>

                <div className="pl-report-card">
                    <div className="pl-table-scroll" ref={tableContainerRef}>
                        {periodMode === 'custom' ? (
                            // Custom range: exactly start_date..end_date. Account / Amount / Ratio only (backend
                            // total_amount and ratio); no YTD, prior-year or budget columns.
                            <table className="pl-table pl-table--custom">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} className="pl-cell--desc pl-col-desc" scope="col">{t("table.description")}</th>
                                        <th colSpan={2} className="pl-col-group" scope="colgroup">
                                            <span className="pl-col-label">{t("filter.periodMode.custom")}</span>
                                            <span className="pl-col-date">{customColumnRange}</span>
                                        </th>
                                    </tr>
                                    <tr>
                                        <th className="pl-col-sub pl-group-start" scope="col">{t("table.amount")}</th>
                                        <th className="pl-col-sub" scope="col">{t("table.ratio")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {glProfitLossList.length ? reportRows.map((row) => {
                                        const { data, code } = row;
                                        return (
                                            <tr className={`pl-row pl-row--${ROW_VARIANTS[code] ?? 'account'}`} key={data['account_chart']['id']}>
                                                {renderDescription(row)}
                                                {HEADER_LEVELS[code] !== undefined ? (<td colSpan={2}></td>) : (
                                                    <>
                                                        <td className="pl-group-start">
                                                            {renderFigure(
                                                                formatAmount(data['total_amount'], data['account_chart']['account']),
                                                                (code === 'VP' && data['total_amount'] && generatedPeriod?.mode === 'custom')
                                                                    ? () => _handleShowViewRegister(data, generatedPeriod.startDate, true)
                                                                    : undefined
                                                            )}
                                                        </td>
                                                        <td>{renderFigure(percentageFormat((data['ratio'] || 0).toFixed(2)))}</td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    }) : renderEmptyRow(3, lastGenerateEmpty ? t("table.notAvailableCustom") : t("table.selectPeriod"))}
                                </tbody>
                            </table>
                        ) : (
                            <table className="pl-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} className="pl-cell--desc pl-col-desc" scope="col">{t("table.description")}</th>
                                        {monthlyColumnGroups.map(group => (
                                            <th key={group.label} colSpan={group.columns.length} className="pl-col-group" scope="colgroup">
                                                <span className="pl-col-label">{group.label}</span>
                                                <span className="pl-col-date">{group.range}</span>
                                            </th>
                                        ))}
                                    </tr>
                                    <tr>
                                        {monthlyColumnGroups.map(group => group.columns.map((column, index) => (
                                            <th key={`${group.label}-${index}`} className={`pl-col-sub pl-col-sub--${column.kind}${index === 0 ? ' pl-group-start' : ''}`} scope="col">{column.label}</th>
                                        )))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {glProfitLossList.length ? reportRows.map((row) => {
                                        const { data, code } = row;
                                        const account = data['account_chart']['account'];
                                        return (
                                            <tr className={`pl-row pl-row--${ROW_VARIANTS[code] ?? 'account'}`} key={data['account_chart']['id']}>
                                                {renderDescription(row)}
                                                {HEADER_LEVELS[code] !== undefined ? (<td colSpan={12}></td>) : (
                                                    <>
                                                        <td className="pl-group-start">{renderFigure(percentageFormat((data['ratio'] || 0).toFixed(2)))}</td>
                                                        <td>
                                                            {renderFigure(
                                                                formatAmount(data['total_amount'], account),
                                                                (code === 'VP' && data['total_amount'])
                                                                    ? () => _handleShowViewRegister(data, `${year}-${month}-01`, true)
                                                                    : undefined
                                                            )}
                                                        </td>
                                                        <td>{renderFigure(formatAmount(data['current_year_budget_amount'], account))}</td>
                                                        <td>{renderFigure(calculateRatio(data['total_amount'] || 0, data['current_year_budget_amount'] || 0))}</td>

                                                        <td className="pl-group-start">{renderFigure(percentageFormat((data['ytd_ratio'] || 0).toFixed(2)))}</td>
                                                        <td>
                                                            {renderFigure(
                                                                formatAmount(data['ytd_total_amount'], account),
                                                                (code === 'VP' && data['ytd_total_amount'])
                                                                    ? () => _handleShowViewRegister(data, `${year}-01-01`, false)
                                                                    : undefined
                                                            )}
                                                        </td>
                                                        <td>{renderFigure(formatAmount(data['budget_ytd_total_amount'], account))}</td>
                                                        <td>{renderFigure(calculateRatio(data['ytd_total_amount'] || 0, data['budget_ytd_total_amount'] || 0))}</td>

                                                        {/* prior-year groups: Ratio / Amount only, no budget columns */}
                                                        <td className="pl-group-start">{renderFigure(percentageFormat((data['prev_year_ratio'] || 0).toFixed(2)))}</td>
                                                        <td>{renderFigure(formatAmount(data['prev_year_total_amount'], account))}</td>

                                                        <td className="pl-group-start">{renderFigure(percentageFormat((data['prev_ytd_ratio'] || 0).toFixed(2)))}</td>
                                                        <td>{renderFigure(formatAmount(data['prev_ytd_total_amount'], account))}</td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    }) : renderEmptyRow(13, lastGenerateEmpty ? t("table.notAvailable") : t("table.selectPeriod"))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    <p className="pl-note">
                        <i className="fa-solid fa-info pl-note__icon" aria-hidden="true"></i>
                        <span>{t("executiveDashboard.ebitdaNote")}</span>
                    </p>
                </div>
                <ScrollToggle scrollRef={tableContainerRef}/>

                {
                    isShowViewRegister ?
                        <ViewRegisterModal
                            show={isShowViewRegister}
                            onHide={() => _handleCloseViewRegister()}
                            startDate={txnRegisterPayload.startDate}
                            endDate={txnRegisterPayload.endDate}
                            account={txnRegisterPayload.account}
                            hasEditAccess={txnRegisterPayload.hasEditAccess}
                            header={t("transactionsRegister")}
                        /> : <></>
                }
            </div>
        </div>

    )
}

export default ProfitLoss;
